// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import ImportedEMRCard from "./ImportedEMRCard/ImportedEMRCard";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getBirthdateOnly, getFullName } from "src/utils/emr/emrMatchHelper";

// Types
import { OrderType } from "src/types/sharedTypes";
import { ExtendedUnassignedEMRType } from "src/services/emr/types";

// Hooks
import { useUnassignedEMR } from "src/services/emr/hooks/useUnassignedEMR";
import useDebounce from "src/hooks/input/useDebounce";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

const LOADING_NUM_OF_ROWS = 5;
const MAX_NUM_OF_ROWS = 10;

const ImportedEMRs = () => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const {
    data: unassignedEMR,
    isLoading: isLoadingUnassignedEMR,
    isError: isErrorUnassignedEMR,
  } = useUnassignedEMR(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingUnassignedEMR,
    [isLoadingUnassignedEMR]
  );

  const hasEMRs = useMemo(
    () =>
      !isLoading &&
      !isErrorUnassignedEMR &&
      unassignedEMR &&
      unassignedEMR?.jobs?.length > 0,
    [isLoading, isErrorUnassignedEMR, unassignedEMR]
  );

  const hasNoEMRs = useMemo(
    () => (!isLoading && !unassignedEMR?.jobs?.length) || isErrorUnassignedEMR,
    [isLoading, isErrorUnassignedEMR, unassignedEMR]
  );

  const orderByPatientName: Array<keyof ExtendedUnassignedEMRType> = [
    "patientName",
  ];
  const orderByBirthdate: Array<keyof ExtendedUnassignedEMRType> = [
    "patientBirthdate",
  ];
  const orderByImportDate: Array<keyof ExtendedUnassignedEMRType> = [
    "createdAt",
  ];
  const orderByExamType: Array<keyof ExtendedUnassignedEMRType> = ["examType"];
  const orderByExamStatus: Array<keyof ExtendedUnassignedEMRType> = ["status"];

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [order, setOrder] = useState<OrderType>("asc");
  const debouncedTerm = useDebounce(searchTerm);

  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ExtendedUnassignedEMRType>
  >([...orderByPatientName]);

  const headCells = [
    {
      id: "name",
      label: t("Name"),
      gridSize: 2,
      orderUsing: [...orderByPatientName, ...orderByExamType],
    },
    {
      id: "birthdate",
      label: t("Birthdate"),
      gridSize: 2,
      orderUsing: [
        ...orderByBirthdate,
        ...orderByPatientName,
        ...orderByExamType,
      ],
    },
    {
      id: "type",
      label: t("Type"),
      gridSize: 2,
      orderUsing: [...orderByExamType, ...orderByPatientName],
    },
    {
      id: "status",
      label: t("Status"),
      gridSize: 2,
      orderUsing: [...orderByExamStatus, ...orderByPatientName],
    },
    {
      id: "importdate",
      label: t("Import Date"),
      gridSize: 2,
      orderUsing: [
        ...orderByImportDate,
        ...orderByPatientName,
        ...orderByExamType,
      ],
    },
  ];

  // Clear searchbar
  const clearSearchFilter = () => {
    setPage(1);
    setSearchTerm("");
  };

  // Searchbar change
  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (page > 1) setPage(1);
    setSearchTerm(event?.target?.value);
  };

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ExtendedUnassignedEMRType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
      setPage(1);
    },
    [order, orderByOptions]
  );

  const formattedExams: Array<ExtendedUnassignedEMRType> = useMemo(
    () =>
      (unassignedEMR?.jobs ?? [])?.map((emr) => ({
        ...emr,
        patientName:
          getFullName(emr.patientFirstName, emr.patientLastName) || "N/A",
        patientBirthdate: getBirthdateOnly(emr.patientDateOfBirth) || "N/A",
      })),
    [unassignedEMR]
  );

  // Filtered rows
  const filteredRows: Array<ExtendedUnassignedEMRType> = useMemo(() => {
    let filterArray = formattedExams?.filter((exam) => {
      const debouncedLowerCase = debouncedTerm?.toLowerCase();

      const searchTermHasName = exam?.patientName
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasType = exam?.examType
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasDOB = exam?.patientBirthdate
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasStatus = exam?.status
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      const searchTermHasImportDate = exam?.createdAt
        ?.toString()
        ?.toLowerCase()
        ?.includes(debouncedLowerCase);

      return (
        searchTermHasName ||
        searchTermHasType ||
        searchTermHasDOB ||
        searchTermHasStatus ||
        searchTermHasImportDate
      );
    });

    return multiKeyCompare<ExtendedUnassignedEMRType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [formattedExams, order, orderByOptions, debouncedTerm]);

  const hasNoSearchResults = useMemo(
    () => debouncedTerm?.length > 0 && !filteredRows?.length,
    [debouncedTerm, filteredRows]
  );

  // exams to render
  const examsOnCurrentPage = useMemo(() => {
    return getItemsToRender(filteredRows, page, MAX_NUM_OF_ROWS);
  }, [filteredRows, page]);

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  // Only clamp the page back if it's no longer in range (e.g. after a delete
  // shrinks the list) — don't reset it just because filteredRows changed.
  useEffect(() => {
    const pageCount = getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS);
    if (page > pageCount) setPage(pageCount || 1);
  }, [filteredRows, page]);

  return (
    <Box display="flex" width="100%" flexDirection="column">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
        mb="20px"
      >
        <Typography
          height="40px"
          alignContent="center"
          fontSize="16px"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
        >
          {t("Processed Exams")}
        </Typography>

        {(hasEMRs || isLoading) && (
          <ExpandableSearchbar
            id="processed-exams-searchbar"
            isLoading={isLoading}
            searchTerm={searchTerm}
            onChange={onSearchChange}
            onClear={clearSearchFilter}
            searchTooltip={t("Search Exams")}
            openByDefault={true}
          />
        )}
      </Box>

      {isLoading && <TableSkeleton numOfRows={LOADING_NUM_OF_ROWS} />}

      {hasEMRs && (
        <Box display="flex" flexDirection="column" gap="12px">
          <Grid container item xs={12}>
            <TableHeader<ExtendedUnassignedEMRType>
              headCells={headCells}
              order={order}
              orderBy={orderByOptions?.[0] ?? "patientName"}
              onRequestSort={handleRequestSort}
              rowCount={examsOnCurrentPage.length}
              numSelected={0}
            />
          </Grid>
          {examsOnCurrentPage?.map((exam) => (
            <ImportedEMRCard key={exam.jobId} exam={exam} />
          ))}
          <Box mt="12px">
            <CustomPagination
              page={page}
              count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
              onChange={onPaginationChange}
              selectedColorDark="#00000090"
            />
          </Box>
        </Box>
      )}

      {/* Error messages */}
      {!hasNoEMRs && hasNoSearchResults && (
        <ErrorText
          header={t("No Processed Exams Found.")}
          message={t("Change your search term to see more results.")}
        />
      )}
      {hasNoEMRs && (
        <Box display="flex" justifyContent="center" my="60px">
          <Typography textAlign="center">
            {t("No Processed Exams Found.")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ImportedEMRs;
